import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { Check, X, Trash2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { ButtonGroup } from '../ui/button-group';
import { useClimbAdminActions } from './useClimbAdminActions';
import type { Climb } from '../../types/climb';

interface ClimbAdminActionsProps {
  climb: Climb;
  onClimbUpdated?: (updatedClimb: Climb) => void;
  onClimbDeleted?: (climbId: string) => void;
  showAge?: boolean;
  /**
   * Whether confirm and reject are offered. Off for the climb's author, who may
   * edit and delete what they put up but not make it official.
   */
  canReview?: boolean;
}

/**
 * The admin's buttons for a climb, as a row of them — on the climb's own page,
 * in the lightbox and on the crag table. A list row offers the same actions
 * behind a swipe instead (ClimbRow); both run through useClimbAdminActions.
 */
export default function ClimbAdminActions({
  climb,
  onClimbUpdated,
  onClimbDeleted,
  showAge = true,
  canReview = true,
}: ClimbAdminActionsProps) {
  const { t, i18n } = useTranslation();
  const { loading, confirm, reject, destroy, edit, isPending, isDiscarded, available } =
    useClimbAdminActions(climb, { onClimbUpdated, onClimbDeleted });

  const timeAgo = formatDistanceToNow(new Date(climb.created_at), {
    addSuffix: true,
    locale: i18n.language === 'it' ? itLocale : enUS,
  });

  if (!available) {
    // If the climb is neither pending nor active, don't show any admin actions
    return null;
  }
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {showAge && (
        <span className="text-xs text-muted-foreground">{timeAgo}</span>
      )}
      <ButtonGroup>
      {/* Show Confirm and Reject buttons only when status is pending */}
      {canReview && isPending && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={confirm}
            disabled={loading !== null}
            className="h-7 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            {loading === 'confirm' ? (
              <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check className="w-3 h-3" />
            )}
            <span className="ml-1">{t('climbs.admin.confirm')}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={reject}
            disabled={loading !== null}
            className="h-7 px-2 text-xs text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
          >
            {loading === 'reject' ? (
              <div className="w-3 h-3 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <X className="w-3 h-3" />
            )}
            <span className="ml-1">{t('climbs.admin.reject')}</span>
          </Button>
        </>
      )}

      {/* Edit button is always available for non-discarded climbs */}
      {!isDiscarded && (
        <Button
          className="h-7 px-2 text-xs"
          variant="outline"
          size="sm"
          onClick={edit}
          disabled={loading !== null}
        >
          <Pencil className="w-3 h-3" />
          <span className="ml-1">{t('climbs.admin.edit')}</span>
        </Button>
      )}

      {/* Destroy button is always available for admins */}
      {!isDiscarded && (
        <Button
          className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
          variant="outline"
          size="sm"
          onClick={destroy}
          disabled={loading !== null}
        >
          {loading === 'destroy' ? (
            <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Trash2 className="w-3 h-3" />
          )}
          <span className="ml-1">{t('climbs.admin.destroy')}</span>
        </Button>
      )}
      </ButtonGroup>
    </div>
  );
}
